import axios from 'axios';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';
import { checkOnScreenEls } from '../../../../assets/src/js/common/on-screen';
import { toggleExpandable } from '../../../../assets/src/js/common/expandable';

import '../sass/citrus-properties.scss';

(() => {
    const $citrusProperties = document.querySelectorAll('.citrus-properties');

    if (!$citrusProperties) return;

    $citrusProperties.forEach($citrusProperty => {
        const $filterForm = $citrusProperty.querySelector('.citrus-properties__filter');
        const $filterFormAllInputs = $citrusProperty?.querySelectorAll('.citrus-properties__filter-field input, .citrus-properties__filter-field select');
        const $filterFormChangeInputs = $citrusProperty?.querySelectorAll('.citrus-properties__filter-field input[type="checkbox"], .citrus-properties__filter-field input[type="radio"], .citrus-properties__filter-field select');
        const $filterBtn = $citrusProperty?.querySelector('.citrus-properties__filter-btn');
        const $filterExpandable = $citrusProperty?.querySelector('.citrus-properties__filter-expandable');

        const $filterPropertiesField = $citrusProperty?.querySelector('.citrus-properties__location-filter input');

        const $propertiesGrid = $citrusProperty.querySelector('.citrus-properties__properties-grid');

        const $mapInner = $citrusProperty.querySelector('.citrus-properties__map-inner');

        const $loadMoreForm = $citrusProperty.querySelector('.citrus-properties__load-more-form');
        const $loadMorePageInput = $loadMoreForm?.querySelector('[name="properties_page"]');
        const $loadMoreFormAllInputs = $loadMoreForm?.querySelectorAll('.citrus-properties__load-more-field');

        const $script = $citrusProperty.querySelector('.citrus-properties__script');

        const script = $script ? JSON.parse($script.textContent) : null;

        let loadingMore = false;
        let map;
        let markerCluster;

        const filterController = new AbortController();

        const loadMoreObserver = new IntersectionObserver(entries => {
            if (!entries) return;

            entries.forEach(entry => {
                if (!entry.isIntersecting) return;

                loadMore();
            });
        }, {
            threshold: 0
        });

        const addMapMarkers = async (properties, recentreMap = true) => {
            const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
            
            if (markerCluster) {
                markerCluster.setMap(null);
            }

            if (recentreMap) {
                const bounds = new google.maps.LatLngBounds();

                properties.forEach(property => {
                    const position = new google.maps.LatLng(property?.location?.lat, property?.location?.lng);

                    bounds.extend(position);
                });

                // Don't zoom in too far on only one marker
                if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                    const extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
                    const extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
                    
                    bounds.extend(extendPoint1);
                    bounds.extend(extendPoint2);
                }

                map.fitBounds(bounds);
            }

            const mapMarkers = properties.map(property => {
                const position = new google.maps.LatLng(property?.location?.lat, property?.location?.lng);

                const content = document.createElement('div');
    
                content.className = 'citrus-properties__map-marker background-color-primary font-size-14';
                content.textContent = property?.price ? `£${property?.price.toLocaleString()}` : '';
    
                const marker = new AdvancedMarkerElement({
                    position,
                    content
                });
    
                marker.addListener('click', () => {
                    if (!property?.url) return;

                    window.location.href = property.url;
                });

                return marker;
            });

            markerCluster = new MarkerClusterer({
                markers: mapMarkers, 
                map,
                onClusterClick: (e, cluster) => {
                    map.fitBounds(cluster.bounds);

                    return true;
                },
                algorithm: new GridAlgorithm({ 
                    maxDistance: 40000,
                    gridSize: 48
                }),
                renderer: {
                    render: function ({ count, position }, stats) {
                        const primaryColour = getComputedStyle(document.documentElement).getPropertyValue('--primary');
        
                        const svg = window.btoa(`
                            <svg fill="${primaryColour}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                                <circle cx="24" cy="24" r="24" />    
                            </svg>`);
                            
                        return new google.maps.Marker({
                            position,
                            icon: {
                                url: `data:image/svg+xml;base64,${svg}`,
                                scaledSize: new google.maps.Size(48, 48),
                            },
                            label: {
                                text: String(count),
                                color: '#FFFFFF',
                                fontSize: '14px',
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 'bold'
                            },
                            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                        });
                    },
                },
            });
        }

        const setFilterURLParams = ({
            page,
            filters,
        }) => {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);

            params.delete('properties_page');

            if (page) {
                params.append('properties_page', page);
            }

            if (filters) {
                for (const [key, value] of Object.entries(filters)) {
                    params.delete(key);
                    params.append(key, value);
                }
            }

            window.history.replaceState(null, document.title, url.pathname + (params.toString() ? '?' + params.toString() : ''));
        };

        const submitFilterForm = () => {
            if (!$propertiesGrid || !$filterForm) return;

            const $nonce = $filterForm.querySelector('[name="citrus_load_more_citrus_properties_wpnonce"]');
            const $postsPerPage = $filterForm.querySelector('[name="posts_per_page"]');

            const nonceValue = $nonce?.value;
            const postsPerPageValue = $postsPerPage?.value;

            const filerValues = {};

            if ($filterFormAllInputs) {
                $filterFormAllInputs.forEach($filterFormInput => {
                    if ($filterFormInput?.type === 'radio' && !$filterFormInput?.checked) return;

                    const value = $filterFormInput?.type !== 'checkbox' || $filterFormInput?.checked ? $filterFormInput?.value : '';

                    filerValues[$filterFormInput?.name] = value;

                    const $loadMoreFormInput = $loadMoreForm?.querySelector(`[name="${$filterFormInput?.name}"]`);

                    if ($loadMoreFormInput) {
                        $loadMoreFormInput.value = value;
                    }
                });
            }
            
            $propertiesGrid.classList.add('citrus-properties__grid--loading');

            axios.get(GLOBALS.AJAX_URL, {
                params: {
                    action: 'citrus_property_ajax_get_all', 
                    nonce: nonceValue,
                    posts_per_page: postsPerPageValue,
                    page: 1,
                    ...filerValues,
                },
                signal: filterController.signal,
            }).then(response => {
                if (!response?.data?.success) return;

                $propertiesGrid.innerHTML = response?.data?.properties ?? '';

                setFilterURLParams({
                    filters: filerValues,
                });

                if (response?.data?.blog_posts) {
                    $propertiesGrid.innerHTML = response.data.blog_posts;
                }

                checkOnScreenEls();

                if ($loadMorePageInput) {
                    $loadMorePageInput.value = 2;
                }

                // Show/hide load more button
                if (response?.data?.found_posts >= response?.data?.post_count || !response?.data?.properties) {
                    $loadMoreForm.classList.remove('citrus-properties__load-more-form--show');
                } else {
                    $loadMoreForm.classList.add('citrus-properties__load-more-form--show');
                }

                addMapMarkers(response?.data?.map_properties ?? [], !response?.data?.map_bounds?.length);

                if (response?.data?.map_bounds?.length) {
                    const bounds = new google.maps.LatLngBounds();
    
                    response?.data?.map_bounds.forEach(map_bounds => {
                        const position = new google.maps.LatLng(map_bounds?.lat, map_bounds?.lng);
    
                        bounds.extend(position);
                    });
    
                    // Don't zoom in too far on only one marker
                    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                        const extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
                        const extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
                        
                        bounds.extend(extendPoint1);
                        bounds.extend(extendPoint2);
                    }
    
                    map.fitBounds(bounds);
                }
            }).finally(() => {
                $propertiesGrid.classList.remove('citrus-properties__grid--loading');

                loadingMore = false;

                loadMore();
            });
        };

        const loadMore = () => {
            if (loadingMore && $loadMoreForm.classList.contains('citrus-properties__load-more-form--show')) {
                setTimeout(() => {
                    loadMore();
                }, 100);
            }

            if (loadingMore || !$propertiesGrid || !$loadMoreForm || !$loadMoreForm.classList.contains('citrus-properties__load-more-form--show') || $loadMoreForm.getBoundingClientRect().top > window.innerHeight) return;

            const $nonce = $loadMoreForm.querySelector('[name="citrus_load_more_citrus_properties_wpnonce"]');
            const $postsPerPage = $loadMoreForm.querySelector('[name="posts_per_page"]');

            const nonceValue = $nonce?.value;
            const postsPerPageValue = $postsPerPage?.value;
            const pageValue = $loadMorePageInput?.value;

            const filerValues = {};

            if ($loadMoreFormAllInputs) {
                $loadMoreFormAllInputs.forEach($loadMoreFormAllInput => {
                    if ($loadMoreFormAllInput?.type === 'radio' && !$loadMoreFormAllInput?.checked) return;

                    const value = $loadMoreFormAllInput?.type !== 'checkbox' || $loadMoreFormAllInput?.checked ? $loadMoreFormAllInput?.value : '';

                    filerValues[$loadMoreFormAllInput?.name] = value;

                    const $loadMoreFormInput = $loadMoreForm?.querySelector(`[name="${$loadMoreFormAllInput?.name}"]`);

                    if ($loadMoreFormInput) {
                        $loadMoreFormInput.value = value;
                    }
                });
            }

            $propertiesGrid.classList.add('citrus-properties__grid--loading');

            loadingMore = true;

            axios.get(GLOBALS.AJAX_URL, {
                params: {
                    action: 'citrus_property_ajax_get_all', 
                    nonce: nonceValue,
                    posts_per_page: postsPerPageValue,
                    page: pageValue,
                    ...filerValues,
                },
                signal: filterController.signal,
            }).then(response => {
                if (!response?.data?.success) return;

                if (response?.data?.properties) {
                    $propertiesGrid.insertAdjacentHTML('beforeend', response.data.properties);

                    setFilterURLParams({
                        page: pageValue,
                        filters: filerValues,
                    });

                    checkOnScreenEls();
                }

                if (response?.data?.page && $loadMorePageInput) {
                    $loadMorePageInput.value = parseInt(response.data.page) + 1;
                }

                // Show/hide load more button
                if (response?.data?.found_posts >= response?.data?.post_count || !response?.data?.properties) {
                    $loadMoreForm.classList.remove('citrus-properties__load-more-form--show');
                } else {
                    $loadMoreForm.classList.add('citrus-properties__load-more-form--show');
                }
            }).finally(() => {
                $propertiesGrid.classList.remove('citrus-properties__grid--loading');

                loadingMore = false;

                loadMore();
            });
        };

        const initFilter = () => {
            if (!$filterForm) return;

            $filterForm.addEventListener('submit', e => {
                e.preventDefault();

                submitFilterForm();
            });

            if ($filterFormChangeInputs) {
                $filterFormChangeInputs.forEach($filterFormInput => {
                    $filterFormInput.addEventListener('change', submitFilterForm);
                });
            }
        };

        const initLocationInput = () => {
            if (!$filterPropertiesField) return;

            let locationSearchTimeout = null;

            const autocomplete = new google.maps.places.AutocompleteService();

            const $resultsContainer = document.createElement('div');
            const $resultsContainerBackground = document.createElement('div');
            const $resultsContainerInner = document.createElement('div');
            const $resultsContainerList = document.createElement('ul');

            $resultsContainer.classList.add('citrus-properties__location-filter-results');
            $resultsContainerBackground.classList.add('citrus-properties__location-filter-results-background');
            $resultsContainerBackground.classList.add('border-color-light-blue-5');
            $resultsContainerInner.classList.add('citrus-properties__location-filter-results-inner');
            $resultsContainerInner.classList.add('background-color-white');
            $resultsContainerList.classList.add('citrus-properties__location-filter-results-list');

            $resultsContainer.appendChild($resultsContainerInner);

            $resultsContainerInner.appendChild($resultsContainerBackground);
            $resultsContainerInner.appendChild($resultsContainerList);

            $filterPropertiesField.after($resultsContainer);

            $filterPropertiesField.addEventListener('keyup', e => {
                $resultsContainerList.innerHTML = '';
                
                if (locationSearchTimeout) {
                    clearTimeout(locationSearchTimeout);
                }

                if (!e?.target?.value || e.target.value.length < 3) return;

                locationSearchTimeout = setTimeout(() => {
                    autocomplete.getPlacePredictions({
                        input: e?.target?.value,
                        componentRestrictions: {
                            country: 'gb'
                        },
                        types: [
                            'locality',
                            'point_of_interest',
                            'postal_code'
                        ]
                    }, (predictions, status) => {
                        if (status !== 'OK') return;

                        predictions?.forEach(prediction => {
                            $resultsContainerList.insertAdjacentHTML(
                                'beforeend', 
                                `<li><button class="background-color-transparent font-size-14 hover-background-color-blue hover-color-white" type="button" data-location="${prediction?.description}">${prediction?.description}</button></li>`
                            );
                        });

                        const $locationResultsButtons = $resultsContainer.querySelectorAll('button');

                        if ($locationResultsButtons) {
                            $locationResultsButtons.forEach($locationResultsButton => {
                                $locationResultsButton.addEventListener('click', () => {
                                    $filterPropertiesField.value = $locationResultsButton?.dataset?.location ?? '';

                                    $resultsContainerList.innerHTML = '';

                                    submitFilterForm();
                                });
                            });
                        }
                    });
                }, 250);
            });

            document.addEventListener('click', e => {
                if (e.target === $filterPropertiesField) return;

                $resultsContainerList.innerHTML = '';
            });
        };

        const initFilterExpandable = () => {
            if (!$filterBtn || !$filterExpandable) return;

            $filterBtn.addEventListener('click', () => {
                toggleExpandable($filterExpandable);
            });

            $filterExpandable.addEventListener('expandableopen', () => {
                $filterBtn.classList.add('citrus-properties__filter-btn--active');
            });

            $filterExpandable.addEventListener('expandableclose', () => {
                $filterBtn.classList.remove('citrus-properties__filter-btn--active');
            });
        };

        const initLoadMoreForm = () => {
            if (!$loadMoreForm) return;

            $loadMoreForm.addEventListener('submit', e => {
                e.preventDefault();

                loadMore();
            });

            loadMoreObserver.observe($loadMoreForm);
        };

        const initMap = () => {
            if (map) return;

            map = new google.maps.Map($mapInner, {
                center: {
                    lat: 52.868247,
                    lng: -1.099259,
                },
                zoom: 6,
                disableDefaultUI: true,
                zoomControl: true,
                mapId: $mapInner?.id,
            });

            if (script?.properties) {
                addMapMarkers(script.properties, !script?.map_bounds?.length);
            }

            if (script?.map_bounds) {
                const bounds = new google.maps.LatLngBounds();

                script?.map_bounds.forEach(map_bounds => {
                    const position = new google.maps.LatLng(map_bounds?.lat, map_bounds?.lng);

                    bounds.extend(position);
                });

                // Don't zoom in too far on only one marker
                if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                    const extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
                    const extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
                    
                    bounds.extend(extendPoint1);
                    bounds.extend(extendPoint2);
                }

                map.fitBounds(bounds);
            }
        };

        initFilter();
        initLocationInput();
        initFilterExpandable();
        initLoadMoreForm();
        initMap();
    });
})();
